/**
 * PlanChangeReceipt
 *
 * The cart card for a PAID member changing plan. It replaces the generic
 * subtotal/total breakdown, which was actively misleading here: the cart line
 * carries the new plan's recurring price (e.g. "2 519,00 kr / year") while the
 * member is only charged the prorated difference today (e.g. "660,22 kr"), and
 * nothing on the old card connected the two numbers.
 *
 * The card states, in this order:
 *   1. the renewal date does not move (the member's actual question),
 *   2. what the remaining period on the new plan costs,
 *   3. the credit for the time they already paid for,
 *   4. what that leaves to pay today — as the total, not as a footnote,
 *   5. when the full recurring price starts applying.
 *
 * Every amount is server-priced. The charge/credit pair is rendered only when
 * the backend supplied both (they satisfy charge − credit == amount due); when
 * it couldn't price the change, the card degrades to the amount due alone
 * rather than showing a breakdown that doesn't add up.
 */

import React from "react";
import type { CartMerchant, MembershipPlanChange } from "@/types/cart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { formatMinorUnitsAsCurrency } from "@/utils/priceFormat";
import { formatDate } from "@/utils/entitlements";
import { getLocale } from "@/utils/locale-store";
import styles from "./PlanChangeReceipt.module.css";

export interface PlanChangeReceiptProps {
  change: MembershipPlanChange;
  /** Service provider + legal seller disclosure. Null when unconfigured for the site — the line is then omitted. */
  merchant: CartMerchant | null;
  /** The new plan's recurring price, pre-formatted by the cart (e.g. "2 519,00 kr"). */
  recurringPrice: string | null;
  /** Billing-period suffix for the recurring price (e.g. "/ year"). Null for carts with no resolvable period. */
  periodLabel: string | null;
  isConfirming: boolean;
  /** Set when the confirm call failed; rendered inline so the member can retry without losing the card. */
  hasError: boolean;
  onConfirm: () => void;
}

/** Stripe's downgrade behaviour: the difference lands as a credit on the next invoice instead of a charge today. */
const DOWNGRADE_BEHAVIOR = "create_prorations";

export const PlanChangeReceipt: React.FC<PlanChangeReceiptProps> = ({
  change,
  merchant,
  recurringPrice,
  periodLabel,
  isConfirming,
  hasError,
  onConfirm,
}) => {
  const texts = useLocalizedTexts();

  const currency = change.prorationCurrency;
  const amountMinor = change.prorationAmountMinor;

  // Without a currency we can't render any amount; the caller keeps the generic
  // cart card in that case, so this is defence-in-depth only.
  if (!currency || amountMinor === null) return null;

  const money = (minor: number) => formatMinorUnitsAsCurrency(minor, currency);

  const isDowngrade = change.prorationBehavior === DOWNGRADE_BEHAVIOR;
  // A negative amount due means the credit outweighs the new plan's remaining
  // period. Nothing is charged today and Stripe carries the rest forward — show
  // 0 rather than a negative "total", which reads as a refund that isn't coming.
  const dueTodayMinor = Math.max(0, amountMinor);
  const carriedCreditMinor = amountMinor < 0 ? -amountMinor : 0;

  const newPlanName = change.newTierName ?? change.newTierIdentifier;
  const currentPlanName =
    change.currentTierName ??
    t(
      texts,
      "Tracker.Cart.PlanChange.CurrentPlanFallback",
      "your current subscription"
    );

  const renewalDateLabel = change.renewalDate
    ? formatDate(change.renewalDate)
    : null;

  // Both halves or neither: a lone charge row without its credit would restate
  // the same confusion this card exists to remove.
  const hasBreakdown =
    change.prorationChargeMinor !== null &&
    change.prorationCreditMinor !== null;

  const dueTodayLabel = t(
    texts,
    "Tracker.Cart.PlanChange.DueToday",
    "Due today"
  );

  // VAT is a server-side figure: Stripe applies the OSS rate for the member's
  // country to the prorated invoice. A missing value means "we don't know", which
  // must render as no VAT line at all — "VAT 0,00" would be a claim about the
  // charge. The rate is formatted through Intl so "25 %" / "25%" follows the
  // member's locale.
  const vatMinor = change.prorationTaxMinor;
  const vatRateLabel =
    change.prorationTaxRatePercent !== null
      ? new Intl.NumberFormat(getLocale().language, {
          style: "percent",
          maximumFractionDigits: 2,
        }).format(change.prorationTaxRatePercent / 100)
      : null;

  return (
    <div className={styles.receipt} data-component="plan-change-receipt">
      <p className={styles.heading}>
        {isDowngrade
          ? t(
              texts,
              "Tracker.Cart.PlanChange.DowngradeHeading",
              "Your plan change"
            )
          : t(texts, "Tracker.Cart.PlanChange.UpgradeHeading", "Your upgrade")}
      </p>

      {/* The reassurance leads: an upgrade does not restart or shorten the
          period the member already paid for. */}
      <div className={styles.reassurance} role="note">
        <strong className={styles.reassuranceTitle}>
          {renewalDateLabel
            ? t(
                texts,
                "Tracker.Cart.PlanChange.RenewalUnchanged",
                "Your renewal date doesn't change: {date}."
              ).replace("{date}", renewalDateLabel)
            : t(
                texts,
                "Tracker.Cart.PlanChange.RenewalUnchangedNoDate",
                "Your renewal date doesn't change."
              )}
        </strong>
        <span className={styles.reassuranceBody}>
          {t(
            texts,
            "Tracker.Cart.PlanChange.KeepPaidTime",
            "You keep the time you've already paid for — it's deducted from today's price."
          )}
        </span>
      </div>

      {hasBreakdown && (
        <div className={styles.breakdown}>
          <div className={styles.row}>
            <span>
              {t(
                texts,
                "Tracker.Cart.PlanChange.RemainingPeriodRow",
                "{plan} for the remaining period"
              ).replace("{plan}", newPlanName)}
            </span>
            <span className={styles.rowValue}>
              {money(change.prorationChargeMinor as number)}
            </span>
          </div>
          <div className={`${styles.row} ${styles.rowCredit}`}>
            <span>
              {t(
                texts,
                "Tracker.Cart.PlanChange.UnusedCreditRow",
                "Credit for unused time on {plan}"
              ).replace("{plan}", currentPlanName)}
            </span>
            <span className={styles.rowValue}>
              −{money(change.prorationCreditMinor as number)}
            </span>
          </div>
        </div>
      )}

      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>{dueTodayLabel}</span>
        <span className={styles.totalValue}>{money(dueTodayMinor)}</span>
      </div>

      {/* VAT contained in today's charge. Prices are tax-inclusive, so this is a
          breakdown of the total above, not an addition to it — hence "of which".
          Rendered only when Stripe reported tax AND something is actually being
          charged today. */}
      {vatMinor !== null && dueTodayMinor > 0 && (
        <div className={styles.vatRow}>
          <span>
            {vatRateLabel
              ? t(
                  texts,
                  "Tracker.Cart.PlanChange.VatIncludedWithRate",
                  "of which VAT ({rate})"
                ).replace("{rate}", vatRateLabel)
              : t(texts, "Tracker.Cart.PlanChange.VatIncluded", "of which VAT")}
          </span>
          <span className={styles.vatValue}>{money(vatMinor)}</span>
        </div>
      )}

      {/* A downgrade (or an over-credited upgrade) charges nothing now — say
          where the money actually goes instead of leaving a bare 0. */}
      {(isDowngrade || carriedCreditMinor > 0) && (
        <p className={styles.creditNote}>
          {carriedCreditMinor > 0
            ? t(
                texts,
                "Tracker.Cart.PlanChange.DowngradeCreditNote",
                "A credit of {amount} will be applied to your next invoice."
              ).replace("{amount}", money(carriedCreditMinor))
            : t(
                texts,
                "Tracker.Cart.PlanChange.NoChargeToday",
                "Nothing to pay today."
              )}
        </p>
      )}

      {recurringPrice && (
        <div className={styles.recurringRow}>
          <span>
            {renewalDateLabel
              ? t(
                  texts,
                  "Tracker.Cart.PlanChange.RecurringFrom",
                  "Then from {date}"
                ).replace("{date}", renewalDateLabel)
              : t(
                  texts,
                  "Tracker.Cart.PlanChange.RecurringFromNoDate",
                  "Then at each renewal"
                )}
          </span>
          <span className={styles.recurringValue}>
            {periodLabel ? `${recurringPrice} ${periodLabel}` : recurringPrice}
          </span>
        </div>
      )}

      {hasBreakdown && (
        <details className={styles.explainer}>
          <summary className={styles.explainerSummary}>
            {t(
              texts,
              "Tracker.Cart.PlanChange.HowIsThisCalculated",
              "How is this calculated?"
            )}
          </summary>
          <p className={styles.explainerBody}>
            {renewalDateLabel
              ? t(
                  texts,
                  "Tracker.Cart.PlanChange.CalculationExplainer",
                  "The time you've already paid for on {currentPlan} is deducted from what {newPlan} costs until {date}. Today you only pay the difference."
                )
                  .replace("{currentPlan}", currentPlanName)
                  .replace("{newPlan}", newPlanName)
                  .replace("{date}", renewalDateLabel)
              : t(
                  texts,
                  "Tracker.Cart.PlanChange.CalculationExplainerNoDate",
                  "The time you've already paid for on {currentPlan} is deducted from what {newPlan} costs for the rest of the period. Today you only pay the difference."
                )
                  .replace("{currentPlan}", currentPlanName)
                  .replace("{newPlan}", newPlanName)}
          </p>
        </details>
      )}

      <button
        type="button"
        className={styles.confirmButton}
        onClick={onConfirm}
        disabled={isConfirming}
        aria-busy={isConfirming ? "true" : undefined}
      >
        {isConfirming
          ? t(texts, "Tracker.Cart.PlanChange.Confirming", "Confirming…")
          : dueTodayMinor > 0
            ? t(
                texts,
                "Tracker.Cart.PlanChange.ConfirmWithAmount",
                "Confirm change – pay {amount}"
              ).replace("{amount}", money(dueTodayMinor))
            : t(
                texts,
                "Tracker.Cart.PlanChange.ConfirmButton",
                "Confirm plan change"
              )}
      </button>

      {hasError && (
        <p className={styles.error} role="alert">
          {t(
            texts,
            "Tracker.Cart.PlanChange.Error",
            "Couldn't change your plan. Please try again or update your payment method."
          )}
        </p>
      )}

      {dueTodayMinor > 0 && (
        <p className={styles.fine}>
          {t(
            texts,
            "Tracker.Cart.PlanChange.ChargedToSavedCard",
            "The amount is charged to your saved card immediately."
          )}
        </p>
      )}

      {/* Who the member is actually buying from. The seller is the Stripe account
          holder, i.e. the name that will appear on their card statement — worth
          stating before they pay, not after. */}
      {merchant && (
        <p className={styles.fine}>
          {t(
            texts,
            "Tracker.Cart.ServiceProvider",
            "Service provider: {provider}, sold by {seller}."
          )
            .replace("{provider}", merchant.serviceProviderName)
            .replace("{seller}", merchant.sellerName)}
        </p>
      )}
    </div>
  );
};

export default PlanChangeReceipt;
